// Filter + pagination controls shared by the Module 06 list screens (§4).
// `Every list: cursor pagination` (§4 preamble) — one pager, one facility chip,
// so the ten screens page identically.

import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/button";
import { useFacilities } from "../hooks/inventory-queries";

/**
 * §4.1/§4.3/§4.4 facility filter chip. Defaults to "all facilities the caller
 * can see": the API narrows every read to the caller's facility scope anyway
 * (D-043 service-layer narrowing), so an unset chip is not a wider read — it is
 * "whatever you are entitled to". A single-facility tenant renders no chip at
 * all rather than a one-option select.
 */
export function FacilityChip({
  value,
  onChange,
  allLabel,
}: {
  value: string;
  onChange: (next: string) => void;
  allLabel?: string | undefined;
}) {
  const { t } = useTranslation();
  const facilities = useFacilities();
  const items = facilities.data?.items ?? [];
  if (items.length <= 1) return null;
  return (
    <label className="filter-chip">
      <span className="filter-chip-label">{t("inventory.filters.facility")}</span>
      <select
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
        }}
      >
        <option value="">{allLabel ?? t("inventory.filters.allFacilities")}</option>
        {items.map((facility) => (
          <option key={facility.id} value={facility.id}>
            {facility.name}
          </option>
        ))}
      </select>
    </label>
  );
}

/**
 * Cursor pager (§4 preamble / §5 "cursor pagination"). The caller owns a cursor
 * STACK so "previous" is exact rather than a re-query guess; this component
 * only reports the two intents.
 */
export function CursorPager({
  canPrev,
  nextCursor,
  onPrev,
  onNext,
}: {
  canPrev: boolean;
  nextCursor: string | null | undefined;
  onPrev: () => void;
  onNext: (cursor: string) => void;
}) {
  const { t } = useTranslation();
  const hasNext = nextCursor !== null && nextCursor !== undefined && nextCursor.length > 0;
  if (!canPrev && !hasNext) return null;
  return (
    <nav className="pager" aria-label={t("inventory.filters.pagination")}>
      <Button disabled={!canPrev} onClick={onPrev}>
        {t("inventory.filters.prev")}
      </Button>
      <Button
        disabled={!hasNext}
        onClick={() => {
          if (hasNext) onNext(nextCursor);
        }}
      >
        {t("inventory.filters.next")}
      </Button>
    </nav>
  );
}
